/**
 * Jul-2026 (User-Wunsch) — Firmen-DNA & Leitfäden (/dealer/firmen-dna).
 *
 * Händler beschreiben Firmenphilosophie, Werte, Verhaltenskodex, Prozesse und
 * Leitfäden (Telefonie, Probefahrt, Leads, Kundenbegegnung) in Fließtext und
 * laden Dokumente hoch. Die Inhalte schulen den KI-Assistenten und werden den
 * Mitarbeitern in den passenden Arbeitsbereichen angezeigt.
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpenText, Plus, Loader2, Trash2, Paperclip, Upload, Save, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

function EntryCard({ entry, canWrite, onChanged }) {
    const [content, setContent] = useState(entry.content || "");
    const [title, setTitle] = useState(entry.title || "");
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/dealer/culture/${entry.id}`, { title, content });
            setDirty(false);
            toast.success("Gespeichert — der KI-Assistent kennt den neuen Stand");
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const del = async () => {
        if (!window.confirm(`„${entry.title}" wirklich löschen?`)) return;
        setBusy(true);
        try { await api.delete(`/dealer/culture/${entry.id}`); toast.message("Eintrag gelöscht"); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const upload = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setBusy(true);
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                await api.post(`/dealer/culture/${entry.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(`${files.length} Datei(en) hochgeladen & für die KI ausgelesen`);
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen"); }
        finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid={`culture-entry-${entry.id}`}>
            <div className="flex items-start gap-2">
                {canWrite ? (
                    <Input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                        className="font-bold border-0 border-b border-transparent focus:border-gray-300 rounded-none px-0 h-8 text-sm"
                        data-testid={`culture-entry-title-${entry.id}`} />
                ) : <div className="font-bold text-sm text-gray-800 flex-1">{entry.title}</div>}
                {canWrite && (
                    <Button type="button" size="sm" variant="outline" onClick={del} disabled={busy}
                        className="h-8 border-2 text-red-600 hover:bg-red-50 shrink-0" data-testid={`culture-entry-delete-${entry.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                )}
            </div>
            {canWrite ? (
                <Textarea value={content} onChange={(e) => { setContent(e.target.value); setDirty(true); }} rows={5}
                    placeholder="Fließtext — Philosophie, Werte, Abläufe, Gesprächsleitfäden … (die KI liest mit)"
                    className="mt-2 text-[13px] leading-relaxed" data-testid={`culture-entry-content-${entry.id}`} />
            ) : (
                <p className="mt-2 text-[13px] text-gray-600 whitespace-pre-wrap">{entry.content || "—"}</p>
            )}
            {(entry.attachments || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {entry.attachments.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-sm px-2 py-1" data-testid={`culture-attachment-${entry.id}-${i}`}>
                            <a href={`${PUBLIC_BASE}${a.url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
                                <Paperclip className="w-3 h-3" /> {a.name}
                                {a.chars > 0 && <span className="text-sky-400 font-normal">· KI-gelesen</span>}
                                <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                            {canWrite && (
                                <button type="button" disabled={busy} title="Anhang entfernen"
                                    onClick={async () => {
                                        try { await api.delete(`/dealer/culture/${entry.id}/attachment/${i}`); toast.message("Anhang entfernt"); onChanged(); }
                                        catch (err) { toast.error(err.response?.data?.detail || "Entfernen fehlgeschlagen"); }
                                    }}
                                    className="text-red-400 hover:text-red-600 ml-0.5 font-black" data-testid={`culture-attachment-delete-${entry.id}-${i}`}>×</button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            {canWrite && (
                <div className="flex items-center gap-2 mt-3">
                    <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md" className="hidden"
                        onChange={(e) => upload(e.target.files)} data-testid={`culture-upload-input-${entry.id}`} />
                    <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy}
                        className="h-8 text-xs border-2" data-testid={`culture-upload-${entry.id}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Dokument hochladen</>}
                    </Button>
                    {dirty && (
                        <Button type="button" size="sm" onClick={save} disabled={busy}
                            className="h-8 text-xs bg-[#111827] hover:bg-black text-white" data-testid={`culture-save-${entry.id}`}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Speichern</>}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

export default function CompanyCulturePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newArea, setNewArea] = useState("philosophie");
    const [newTitle, setNewTitle] = useState("");
    const [creating, setCreating] = useState(false);

    const load = () => api.get("/dealer/culture")
        .then((r) => setData(r.data))
        .catch(() => toast.error("Firmen-DNA konnte nicht geladen werden"))
        .finally(() => setLoading(false));
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!newTitle.trim()) { toast.error("Bitte Titel eingeben"); return; }
        setCreating(true);
        try {
            await api.post("/dealer/culture", { area: newArea, title: newTitle.trim(), content: "" });
            setNewTitle("");
            toast.success("Eintrag angelegt — jetzt Text schreiben oder Dokument hochladen");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        finally { setCreating(false); }
    };

    const areas = data?.areas || [];
    const items = data?.items || [];
    const canWrite = data?.can_write;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 py-8" data-testid="company-culture-page">
                <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#111827] mb-4"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
                <h1 className="font-display font-black text-3xl text-[#111827] mb-1 flex items-center gap-2">
                    <BookOpenText className="w-7 h-7 text-[#FFCC00]" /> Firmen-DNA &amp; Leitfäden
                </h1>
                <p className="text-sm text-gray-500 mb-2">Beschreiben Sie Philosophie, Werte, Verhaltenskodex, Prozesse &amp; Leitfäden Ihres Unternehmens — als Fließtext oder Dokument-Upload (PDF/DOCX/TXT).</p>
                <div className="bg-orange-50 border border-orange-200 rounded-sm px-3 py-2 mb-6 text-[12px] text-orange-800 flex items-start gap-2" data-testid="culture-ai-note">
                    <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" />
                    <span><b>So wirken die Inhalte:</b> Der KI-Assistent kennt Ihr Unternehmen und antwortet in Ihrem Geist. Mitarbeiter sehen die passenden Leitfäden direkt bei der Arbeit — z. B. bei der Lead-Bearbeitung, bei Probefahrten und beim Telefonieren. Gilt für alle Standorte.</span>
                </div>

                {canWrite && (
                    <div className="bg-white border-2 border-[#111827] rounded-sm p-3 mb-6 flex flex-col sm:flex-row gap-2" data-testid="culture-new-form">
                        <select value={newArea} onChange={(e) => setNewArea(e.target.value)}
                            className="h-9 text-sm border-2 border-gray-200 rounded-sm px-2 font-bold" data-testid="culture-new-area">
                            {areas.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
                        </select>
                        <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && create()}
                            placeholder="Titel — z. B. „Unser Telefon-Leitfaden“" className="h-9 flex-1" data-testid="culture-new-title" />
                        <Button type="button" onClick={create} disabled={creating}
                            className="h-9 bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827] font-bold" data-testid="culture-new-submit">
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Eintrag anlegen</>}
                        </Button>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…</div>
                ) : (
                    <div className="space-y-8">
                        {areas.map((a) => {
                            const list = items.filter((e) => e.area === a.code);
                            if (!list.length && !canWrite) return null;
                            return (
                                <div key={a.code} data-testid={`culture-area-${a.code}`}>
                                    <h2 className="text-xs font-black uppercase tracking-wider text-gray-500 mb-2">{a.label}</h2>
                                    {list.length ? (
                                        <div className="space-y-3">
                                            {list.map((e) => <EntryCard key={e.id} entry={e} canWrite={canWrite} onChanged={load} />)}
                                        </div>
                                    ) : (
                                        <p className="text-[12px] text-gray-400 border border-dashed border-gray-200 rounded-sm px-3 py-2">Noch keine Inhalte — oben einen Eintrag für diesen Bereich anlegen.</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
